<template>
  <v-dialog v-model="dialog" max-width="960" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        Carga masiva de precios
        <v-spacer />
        <v-btn icon @click="dialog = false">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <!-- Empresa / Marca -->
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete
              v-model="data.empresa"
              label="Empresa"
              :items="familias"
              item-text="nombre"
              return-object
              hide-details
              outlined
              dense
              clearable
              @change="onEmpresaChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete
              v-model="data.marca"
              label="Marca"
              :items="marcasOpciones"
              item-text="nombre"
              item-value="id"
              hide-details
              outlined
              dense
              clearable
              @change="onMarcaChange"
            />
          </v-col>
        </v-row>

        <!-- Nuevo período -->
        <v-row dense class="mt-2 align-center">
          <v-col cols="12" class="pb-0">
            <span class="subtitle-1 font-weight-bold">Nuevo período</span>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <FechaPicker v-model="data.desde" label="Desde" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <FechaPicker v-model="data.hasta" label="Hasta" hide-details clearable />
          </v-col>
        </v-row>

        <!-- Agregar fila -->
        <v-row dense class="mt-2 align-end">
          <v-col cols="12" sm="8" md="6" class="py-2">
            <v-autocomplete
              v-model="nuevoModelo"
              label="Agregar modelo"
              :items="modelosDisponibles"
              item-text="nombre"
              item-value="Modelo_id"
              hide-details
              outlined
              dense
              clearable
              return-object
            />
          </v-col>
          <v-col cols="12" sm="4" md="2" class="py-2">
            <v-btn color="primary" small block :disabled="!nuevoModelo" @click="agregarFila">
              <v-icon left small>fas fa-plus</v-icon>
              Agregar fila
            </v-btn>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <v-data-table
          class="mt-2"
          :headers="headers"
          :items="filas"
          item-key="modelo_id"
          dense
          hide-default-footer
          :items-per-page="-1"
          :loading="cargandoPrecios"
          :no-data-text="data.marca ? 'No hay precios cargados para esta marca' : 'Seleccione empresa y marca'"
        >
          <template v-slot:item.valor_venta_actual="{ item }">
            {{ item.valor_venta_actual | formatMoney }}
          </template>
          <template v-slot:item.valor_venta="{ item }">
            <div class="d-flex justify-end">
              <TextFieldMoney
                v-model="item.valor_venta"
                :properties="{ prefix: '$', outlined: true, dense: true, hideDetails: true, style: 'max-width: 160px' }"
              />
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="item.esNuevo"
              icon
              x-small
              color="error"
              title="Eliminar fila"
              @click="removeFila(item)"
            >
              <v-icon x-small>fas fa-trash</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <div class="d-flex justify-end mt-4">
          <v-btn color="secondary" class="mr-2" @click="dialog = false">
            Volver
          </v-btn>
          <v-btn color="success" :loading="guardando" :disabled="!puedeGuardar" @click="guardar">
            Guardar
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import FechaPicker from '../util/FechaPicker.vue'
import TextFieldMoney from '../util/TextFieldMoney.vue'

export default {
  name: 'NuevoPrecioModeloMasivoV2',
  components: { FechaPicker, TextFieldMoney },
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      data: {
        empresa: null,
        marca: null,
        desde: moment().startOf('month').format('DD/MM/YYYY'),
        hasta: moment().endOf('month').format('DD/MM/YYYY')
      },
      filas: [],
      nuevoModelo: null,
      guardando: false,
      cargandoPrecios: false,
      marcasOpciones: [],
      modelosPorFamilia: []
    }
  },
  computed: {
    dialog: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    ...mapState(['familias', 'empresas']),
    headers() {
      return [
        { text: 'Modelo', value: 'modelo_descripcion', sortable: false },
        { text: 'Cod. Modelo', value: 'modelo_codigo', sortable: false },
        { text: 'Marca', value: 'marca_nombre', sortable: false },
        { text: 'Precio actual', value: 'valor_venta_actual', sortable: false, align: 'end' },
        { text: 'Nuevo precio', value: 'valor_venta', sortable: false, align: 'end', width: '180' },
        { text: '', value: 'actions', sortable: false, width: '50' }
      ]
    },
    modelosDisponibles() {
      const idsEnTabla = new Set(this.filas.map(f => f.modelo_id))
      return (this.modelosPorFamilia || [])
        .filter(m => !m.Inhabilitado && !idsEnTabla.has(m.Modelo_id))
        .map(m => ({ ...m, nombre: `${m.Descripcion} - ${m.Modelo}` }))
    },
    puedeGuardar() {
      return this.data.desde && this.filas.length > 0 && this.filas.some(f => f.modelo_id && f.valor_venta != null)
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.init()
      } else {
        this.reset()
      }
    },
    'data.desde'(val) {
      if (!val) return
      const m = moment(val, 'DD/MM/YYYY')
      if (m.isValid() && m.date() === 1) {
        this.data.hasta = m.endOf('month').format('DD/MM/YYYY')
      }
      if (this.data.marca) {
        this.cargarPreciosExistentes()
      }
    },
    'data.hasta'() {
      if (this.data.marca) {
        this.cargarPreciosExistentes()
      }
    }
  },
  methods: {
    parseFecha(value) {
      if (!value) return null
      const parsed = moment(value, [moment.ISO_8601, 'DD/MM/YYYY', 'YYYY-MM-DD'], true)
      if (parsed.isValid()) return parsed.startOf('day')
      const fallback = moment(value)
      return fallback.isValid() ? fallback.startOf('day') : null
    },
    getPeriodoAnterior() {
      const base = this.parseFecha(this.data.desde) || moment()
      const desde = base.clone().startOf('month').subtract(1, 'month')
      const hasta = desde.clone().endOf('month')
      return { desde, hasta }
    },
    getPeriodoNuevo() {
      const desde = this.parseFecha(this.data.desde)
      const hasta = this.parseFecha(this.data.hasta) || desde
      return { desde, hasta }
    },
    perteneceAPeriodo(precio, periodo) {
      const { desde, hasta } = periodo
      const fechaDesde = this.parseFecha(precio.fecha_desde || precio.fecha_desde_fmt)
      const fechaHasta = this.parseFecha(precio.fecha_hasta || precio.fecha_hasta_fmt) || moment('2999-12-31')
      if (!fechaDesde || !desde || !hasta) return false
      return !(fechaHasta.isBefore(desde, 'day') || fechaDesde.isAfter(hasta, 'day'))
    },
    perteneceAPeriodoAnterior(precio) {
      return this.perteneceAPeriodo(precio, this.getPeriodoAnterior())
    },
    init() {
      this.data.empresa = null
      this.data.marca = null
      this.data.desde = moment().startOf('month').format('DD/MM/YYYY')
      this.data.hasta = moment().endOf('month').format('DD/MM/YYYY')
      this.filas = []
      this.nuevoModelo = null
      this.guardando = false
      this.cargandoPrecios = false
      this.marcasOpciones = []
      this.modelosPorFamilia = []
      if (this.familias && this.familias.length === 1) {
        this.data.empresa = this.familias[0]
        this.onEmpresaChange()
      }
    },
    reset() {
      this.filas = []
      this.nuevoModelo = null
      this.marcasOpciones = []
      this.modelosPorFamilia = []
    },
    async onEmpresaChange() {
      this.data.marca = null
      this.marcasOpciones = []
      this.modelosPorFamilia = []
      this.filas = []
      if (!this.data.empresa || !this.data.empresa.id) return
      this.marcasOpciones = (this.empresas || []).filter(e => e.familia === this.data.empresa.id)
      if (this.marcasOpciones.length === 1) {
        this.data.marca = this.marcasOpciones[0].id
      }
      const res = await this.$store.dispatch('preciosModelosV2/getModelosByFamilia', this.data.empresa.id)
      if (res.exito === 1 && res.data) {
        this.modelosPorFamilia = res.data
      }
      if (this.data.marca) {
        await this.cargarPreciosExistentes()
      }
    },
    async onMarcaChange() {
      this.filas = []
      if (!this.data.marca) return
      await this.cargarPreciosExistentes()
    },
    async cargarPreciosExistentes() {
      if (!this.data.empresa || !this.data.empresa.id || !this.data.marca) return
      this.cargandoPrecios = true
      const payload = {
        empresa: { id: this.data.empresa.id },
        marcas: [this.data.marca]
      }
      const res = await this.$store.dispatch('preciosModelosV2/getPreciosModelos', payload)
      this.cargandoPrecios = false
      if (res.exito !== 1) {
        this.$store.dispatch('show_snackbar', { color: 'error', text: res.message || 'Error al cargar precios' })
        return
      }
      const todosLosPrecios = res.data || []
      const precios = todosLosPrecios.filter(this.perteneceAPeriodoAnterior)
      const preciosNuevoPeriodo = todosLosPrecios.filter(p => this.perteneceAPeriodo(p, this.getPeriodoNuevo()))
      const preciosNuevoPeriodoPorModelo = {}

      preciosNuevoPeriodo.forEach(p => {
        const mid = p.modelo_id
        if (!mid) return
        if (!preciosNuevoPeriodoPorModelo[mid]) {
          preciosNuevoPeriodoPorModelo[mid] = p
          return
        }
        const actual = this.parseFecha(preciosNuevoPeriodoPorModelo[mid].fecha_desde)
        const candidato = this.parseFecha(p.fecha_desde)
        if (candidato && (!actual || candidato.isAfter(actual))) {
          preciosNuevoPeriodoPorModelo[mid] = p
        }
      })

      // Deduplicar por modelo_id, quedarse con el más reciente (el precio actual vigente)
      const porModelo = {}
      precios.forEach(p => {
        const mid = p.modelo_id
        if (!mid) return
        if (!porModelo[mid]) {
          porModelo[mid] = p
        } else {
          const actual = this.parseFecha(porModelo[mid].fecha_desde)
          const candidato = this.parseFecha(p.fecha_desde)
          if (candidato && (!actual || candidato.isAfter(actual))) {
            porModelo[mid] = p
          }
        }
      })
      this.filas = Object.values(porModelo).map(p => ({
        modelo_id: p.modelo_id,
        modelo_codigo: p.Modelo || '',
        modelo_descripcion: p.Descripcion || '',
        marca_nombre: p.marca_nombre || '',
        valor_venta_actual: p.valor_venta,
        valor_venta: preciosNuevoPeriodoPorModelo[p.modelo_id]?.valor_venta ?? null,
        esNuevo: false
      })).sort((a, b) => a.modelo_descripcion.localeCompare(b.modelo_descripcion, 'es', { sensitivity: 'base' }))
    },
    agregarFila() {
      if (!this.nuevoModelo) return
      const m = this.nuevoModelo
      this.filas.push({
        modelo_id: m.Modelo_id,
        modelo_codigo: m.Modelo || '',
        modelo_descripcion: m.Descripcion || '',
        marca_nombre: m.empresa_nombre || m.marca_nombre || '',
        valor_venta_actual: null,
        valor_venta: null,
        esNuevo: true
      })
      this.nuevoModelo = null
    },
    removeFila(item) {
      const idx = this.filas.findIndex(f => f.modelo_id === item.modelo_id && f.esNuevo)
      if (idx >= 0) this.filas.splice(idx, 1)
    },
    async guardar() {
      if (!this.data.desde) {
        this.$store.dispatch('show_snackbar', { color: 'warning', text: 'Ingrese la fecha desde.' })
        return
      }
      if (!this.data.empresa || !this.data.marca) {
        this.$store.dispatch('show_snackbar', { color: 'warning', text: 'Seleccione empresa y marca.' })
        return
      }
      const filasValidas = this.filas.filter(f => f.modelo_id && f.valor_venta != null)
      if (filasValidas.length === 0) {
        this.$store.dispatch('show_snackbar', { color: 'warning', text: 'Complete al menos una fila con modelo y precio.' })
        return
      }
      this.guardando = true
      const payload = filasValidas.map(f => ({
        modelo_id: f.modelo_id,
        valor_venta: Number(f.valor_venta),
        fecha_desde: this.data.desde,
        fecha_hasta: this.data.hasta || this.data.desde
      }))
      const res = await this.$store.dispatch('preciosModelosV2/postPreciosModelosBatch', payload)
      this.guardando = false
      if (res.exito === 1) {
        this.$emit('guardado', { count: res.data?.count || filasValidas.length })
        this.dialog = false
        this.$store.dispatch('show_snackbar', { color: 'success', text: res.message || 'Precios guardados correctamente.' })
      } else {
        this.$store.dispatch('show_snackbar', { color: 'error', text: res.message || 'Error al guardar' })
      }
    }
  },
  filters: {
    formatMoney(v) {
      if (v == null) return '—'
      return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v)
    }
  }
}
</script>
